import { Component, ViewChild, AfterViewInit } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import { MatDialog } from '@angular/material/dialog';
import { ZentraAdminDeleteDialogComponent } from '../zentra-admin-delete-dialog/zentra-admin-delete-dialog.component';
import { Router, ActivatedRoute } from '@angular/router';
import { ZentraAdminIntegratorInviteDialogComponent } from '../zentra-admin-integrator-invite-dialog/zentra-admin-integrator-invite-dialog.component';

@Component({
  selector: 'app-zentra-admin-landing-page',
  templateUrl: './zentra-admin-landing-page.component.html',
  styleUrls: ['./zentra-admin-landing-page.component.scss'],
  standalone: false,
})
export class ZentraAdminLandingPageComponent implements AfterViewInit {
  displayedColumns: string[] = [
    'dateCreated',
    'subscriptionIdentifier',
    'productName',
    'configuredComps',
    'integratorName',
    'purchaserEmail',
    'actions',
  ];
  poSubsDisplayedColumns: string[] = [
    'companyName',
    'customerEmail',
    'propertyName',
    'sku',
    'subStartDate',
    'poNumber',
    'actions',
  ];
  dataSource = new MatTableDataSource<{ recordId: string }>();
  poSubsDataSource = new MatTableDataSource<{ recordId: string }>();
  showZentraAdminStepper = false;
  selectedTabIndex = 0;
  poSubsDataLoaded = false;

  @ViewChild('pendingPaginator') paginator!: MatPaginator;
  @ViewChild('pendingSort') sort!: MatSort;
  @ViewChild('poPaginator') poPaginator!: MatPaginator;
  @ViewChild('poSort') poSort!: MatSort;

  constructor(
    readonly backend: BackendAccessService,
    public dialog: MatDialog,
    readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}
  /* istanbul ignore next */
  ngOnInit() {
    console.log('Zentra Admin Landing Page - ngOnInit called');

    // Watch for query parameter changes (fires on every navigation, not just first load)
    this.route.queryParams.subscribe((params) => {
      console.log('Query params changed:', params);

      if (params['tab']) {
        const tabIndex = parseInt(params['tab'], 10);
        this.selectedTabIndex = tabIndex;

        // Check if refresh is requested
        if (params['refresh'] === 'true' && tabIndex === 1) {
          console.log('Refresh requested - forcing PO data reload');
          this.poSubsDataLoaded = false; // Force reload of PO subs data
          // Directly load the data instead of relying on tab change
          this.loadPOSubsData();
        } else if (tabIndex === 1 && !this.poSubsDataLoaded) {
          // Initial load of PO data if not already loaded
          console.log('Initial load of PO data');
          this.loadPOSubsData();
        }
      }
    });

    this.backend.getPendingSubConfigurations().subscribe({
      next: (data) => {
        this.dataSource.data = data.map((item: { metafields: any }) => ({
          ...item,
          metafields: item.metafields || {},
        }));
        console.log('Data:', this.dataSource.data);
      },
      error: (error) => {
        console.error('Error fetching data:', error);
      },
      complete: () => {
        console.log('Data fetching completed');
      },
    });
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  /* istanbul ignore next */
  applyInputFilter(event: Event, column: string) {
    const filterValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();

    // If filter is cleared, reset to show all data
    if (!filterValue) {
      this.dataSource.filter = '';
      return;
    }

    this.dataSource.filterPredicate = (data: any, filter) => {
      switch (column) {
        case 'dateCreated':
          return data.createdDate?.toLowerCase().includes(filter);
        case 'subscriptionIdentifier':
          return data.subscriptionIdentifier?.toLowerCase().includes(filter);
        case 'productName':
          return data.subscriptionData?.product_name
            ?.toLowerCase()
            .includes(filter);
        case 'configuredComps':
          return data.subscriptionData.components?.some((component: any) =>
            component.name?.toLowerCase().includes(filter)
          );
        case 'integratorName':
          return data.subscriptionData.metafields?.integratorName
            ?.toLowerCase()
            .includes(filter);
        case 'purchaserEmail':
          return data.purchaserEmail?.toLowerCase().includes(filter);
        default:
          return false;
      }
    };

    this.dataSource.filter = filterValue;
  }
  /* istanbul ignore next */
  applyDateFilter(selectedDate: Date | null) {
    if (!selectedDate) {
      // Clear filter if date cleared
      this.dataSource.filter = '';
      return;
    }

    // Format date to string matching your data format (e.g. 'YYYY-MM-DD')
    const filterDateStr = selectedDate.toISOString().split('T')[0];

    this.dataSource.filterPredicate = (data: any, filter: string) => {
      if (!data.createdDate) return false;
      const dataDateStr = new Date(data.createdDate)
        .toISOString()
        .split('T')[0];
      return dataDateStr === filter;
    };

    this.dataSource.filter = filterDateStr;
  }

  /* istanbul ignore next */
  editItem(element: any) {
    this.router.navigate(['/zentra-subscriptions-config', element.recordId]);
  }

  /* istanbul ignore next */
  editPOItem(element: any) {
    this.router.navigate(['/zentra-po-subscription', element.recordId || element.id || 'test']);
  }

  /* istanbul ignore next */
  openIntegratorInvite() {
    this.dialog.open(ZentraAdminIntegratorInviteDialogComponent, {
      width: '800px',
    });
  }

  /* istanbul ignore next */
  deleteItem(element: any) {
    const dialogRef = this.dialog.open(ZentraAdminDeleteDialogComponent, {
      width: '500px',
      data: { recordId: element.recordId },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const recordId = element.recordId;
        console.log('Delete Item:', recordId);

        this.backend.deleteSubConfigurations(recordId).subscribe({
          next: () => {
            console.log(`Record ${recordId} deleted successfully.`);
            this.dataSource.data = this.dataSource.data.filter(
              (item: { recordId: string }) => item.recordId !== recordId
            );
          },
          error: (error) => {
            console.error('Error deleting record:', error);
          },
        });
      }
    });
  }

  /* istanbul ignore next */
  createSubscription() {
    console.log('Create Subscription button clicked');
    this.showZentraAdminStepper = true;
  }
  /* istanbul ignore next */
  closeStepper() {
    this.showZentraAdminStepper = false;
  }

  /* istanbul ignore next */
  onTabChange(index: number) {
    console.log('Tab changed to:', index, 'poSubsDataLoaded:', this.poSubsDataLoaded);
    if (index === 1) {
      if (!this.poSubsDataLoaded) {
        console.log('Loading PO subscriptions data...');
        this.loadPOSubsData();
      }
    }
  }

  /* istanbul ignore next */
  loadPOSubsData() {
    console.log('loadPOSubsData called - fetching fresh data from API');
    this.backend.getPOSubs().subscribe({
      next: (data) => {
        console.log('Received PO subs data from API:', data);
        console.log('First item property name:', data[0]?.propertyName);

        // Create a new array reference to force change detection
        this.poSubsDataSource.data = [...data];

        // Reset paginator and sort
        setTimeout(() => {
          if (this.poPaginator) {
            this.poSubsDataSource.paginator = this.poPaginator;
            this.poPaginator.firstPage(); // Go back to first page
          }
          if (this.poSort) {
            this.poSubsDataSource.sort = this.poSort;
          }
        }, 0);

        this.poSubsDataLoaded = true;
        console.log('PO Subs Data loaded into table. Row count:', this.poSubsDataSource.data.length);
      },
      error: (error) => {
        console.error('Error fetching PO subs data:', error);
      },
      complete: () => {
        console.log('PO subs data fetching completed');
      },
    });
  }

  /* istanbul ignore next */
  applyPOInputFilter(event: Event, column: string) {
    const filterValue = (event.target as HTMLInputElement).value
      .trim()
      .toLowerCase();

    // If filter is cleared, reset to show all data
    if (!filterValue) {
      this.poSubsDataSource.filter = '';
      return;
    }

    this.poSubsDataSource.filterPredicate = (data: any, filter) => {
      switch (column) {
        case 'companyName':
          return data.companyName?.toLowerCase().includes(filter);
        case 'customerEmail':
          return data.customerEmail?.toLowerCase().includes(filter);
        case 'propertyName':
          return data.propertyName?.toLowerCase().includes(filter);
        case 'sku':
          return data.sku?.toLowerCase().includes(filter);
        case 'poNumber':
          return data.poNumber?.toLowerCase().includes(filter);
        case 'subStartDate':
          return data.subStartDate?.toLowerCase().includes(filter);
        default:
          return false;
      }
    };

    this.poSubsDataSource.filter = filterValue;
  }

  /* istanbul ignore next */
  applyPODateFilter(selectedDate: Date | null) {
    if (!selectedDate) {
      // Clear filter if date cleared
      this.poSubsDataSource.filter = '';
      // Reset filter predicate to default
      this.poSubsDataSource.filterPredicate = (data: any, filter: string) => {
        const dataStr = JSON.stringify(data).toLowerCase();
        return dataStr.includes(filter);
      };
      return;
    }

    // Format date to string matching your data format (e.g. 'YYYY-MM-DD')
    const filterDateStr = selectedDate.toISOString().split('T')[0];

    this.poSubsDataSource.filterPredicate = (data: any, filter: string) => {
      if (!data.subStartDate) return false;
      const dataDateStr = new Date(data.subStartDate)
        .toISOString()
        .split('T')[0];
      return dataDateStr === filter;
    };

    this.poSubsDataSource.filter = filterDateStr;
  }
}
